import { useContext } from 'react';
import { Button } from 'reactstrap';
import { useQueryClient } from 'react-query';
import { FaFileExcel } from 'react-icons/fa';

import { PropostaMaterial } from 'models/PropostaMaterial';
import { PropostaMaterialAvulso } from 'models/PropostaMaterialAvulso';

import exportToSheet from 'utils/exportToSheet';
import { arredondaDecimais } from 'utils/arredondaDecimais';

import { PropostaContext } from 'views/pages/Proposta';
import { PropostaConfiguracao } from 'models/PropostaConfiguracao';
import formatCurrency from 'utils/formatCurrency';
import traduzirProdutoAtividade from 'utils/traduzirProdutoAtividade';
import { Tooltip } from '@material-ui/core';
import { MateriaisContext } from '../..';
import { IMateriaisParaDownload } from '../PropostaConfiguracaoMaterial';

interface Props {
  propostaConfiguracoes: PropostaConfiguracao[];
  materiaisParaDownload: IMateriaisParaDownload[];
  className?: string;
  acessoLimitado: boolean;
}

interface Planilha {
  configuracao?: number | string | null;
  codigo_atividade?: number | string | null;
  nome_produto?: string;
  descricao_atividade?: string;
  sequencia?: string;
  codigo_material?: string;
  descricao_material?: string;
  quantidade?: number | string | null;
  valor_unitario?: string | number;
  valor_total?: string | number;
  custo_unitario?: number | string | null;
  custo_total?: number | string | null;
  fornecedor?: string;
  Origem?: string;
}

export function DownloadPlanilhaMaterial({
  propostaConfiguracoes,
  materiaisParaDownload,
  className,
  acessoLimitado,
}: Props): JSX.Element {
  const queryClient = useQueryClient();
  const { propostaVersao } = useContext(PropostaContext);
  const { loadingMateriais } = useContext(MateriaisContext);
  const dadosPlanilha: Planilha[] = [];

  function insereCabecalhoConfiguracao(descricao: string | null) {
    const quebraLinha = {
      codigo_atividade: '',
      descricao_material: '',
    };
    const cabecalho = {
      codigo_atividade: '',
      descricao_material: `${descricao}`,
    };
    dadosPlanilha.push(quebraLinha);
    dadosPlanilha.push(cabecalho);
  }

  function retornaOrigem(
    criadoManualmente: boolean | undefined,
    importacaoMaterialId: number | null,
  ): string {
    if (criadoManualmente === true && importacaoMaterialId !== null) {
      return 'I';
    }

    if (criadoManualmente === true) {
      return 'M';
    }

    return '';
  }

  function insereCabecalhoSequencia(sequencia: number, titulo?: string) {
    dadosPlanilha.push({
      codigo_atividade: '',
      descricao_material: titulo
        ? `Sequência ${sequencia} - ${titulo}`
        : `Sequência ${sequencia}`,
    });
  }

  function insereItem(
    propostaMaterial: PropostaMaterial | PropostaMaterialAvulso,
    isMaterialParaDownload = false,
    configuracaoId?: number,
  ) {
    const {
      atividade,
      material,
      quantidade,
      valor,
      valorTotal,
      importacaoMaterialId,
      produtoAtividade,
    } = propostaMaterial;

    let origem = '';

    if (
      'criadoManualmente' in propostaMaterial &&
      'importacaoMaterial' in propostaMaterial
    ) {
      origem = retornaOrigem(
        propostaMaterial.criadoManualmente,
        importacaoMaterialId,
      );
    }

    const { diteCodigo, descricao, codigoSap } = material;
    let item;
    if (isMaterialParaDownload) {
      const dadosProposta = propostaMaterial as any;
      item = {
        configuracao: configuracaoId,
        codigo_atividade: produtoAtividade
          ? traduzirProdutoAtividade(produtoAtividade, true)
          : '',
        nome_produto: produtoAtividade.significado,
        descricao_atividade: produtoAtividade.significado,
        sequencia: String(dadosProposta.sequenciaAtividade),
        codigo_material: codigoSap ?? diteCodigo,
        descricao_material: `${codigoSap ?? diteCodigo} - ${descricao}`,
        quantidade: arredondaDecimais(quantidade, 3),
        valor_unitario: !acessoLimitado ? valor ?? 0 : '',
        valor_total: !acessoLimitado ? valorTotal ?? 0 : '',
        custo_unitario: Number(dadosProposta.custoUnitario),
        custo_total: Number(dadosProposta.custoTotalImp),
        fornecedor: dadosProposta.material.fabrNome,
        Origem: origem,
      };
    } else {
      // Importado e Avulso
      item = {
        codigo_atividade: produtoAtividade
          ? traduzirProdutoAtividade(produtoAtividade, true)
          : '',
        descricao_material: `${codigoSap ?? diteCodigo} - ${descricao}`,
        quantidade: arredondaDecimais(quantidade, 3),
        valor_unitario: !acessoLimitado ? valor ?? 0 : '',
        valor_total: !acessoLimitado ? valorTotal ?? 0 : '',
        Origem: origem,
      };
    }

    dadosPlanilha.push(item);
  }

  function insereTotalGeral(total?: number) {
    const totalGeral = {
      codigo_atividade: '',
      descricao_material: '',
      quantidade: null,
      valor_unitario: !acessoLimitado ? 'Total geral' : '',
      valor_total: !acessoLimitado ? formatCurrency(total, 2) : '',
    };
    dadosPlanilha.push(totalGeral);
  }

  function insereConfiguracaoPropostaDadosPlanilha() {
    propostaConfiguracoes.forEach(propostaConfiguracao => {
      const configuracaoId = propostaConfiguracao.orcamentoId;

      const configuracao = materiaisParaDownload.find(
        item => item.configuracaoId === configuracaoId,
      );

      if (!configuracao) {
        return;
      }

      if (propostaConfiguracao.fichaTecnica?.pivoCentral) {
        insereCabecalhoConfiguracao(
          `Pivô ${propostaConfiguracao.fichaTecnica.pivoCentral}`,
        );
      }

      configuracao.atividades.forEach(atividade => {
        atividade.sequencias.forEach((sequencia, index, array) => {
          // Cabeçalho da sequência
          insereCabecalhoSequencia(sequencia.sequencia, sequencia.titulo);

          // Materiais da sequência
          sequencia.sequenciaItems.forEach(material => {
            insereItem(material, true, configuracao.configuracaoId);
          });

          // if (acessoLimitado) {
          // Total líquido da sequência
          dadosPlanilha.push({
            codigo_atividade: '',
            descricao_material: '',
            quantidade: null,
            valor_unitario: !acessoLimitado ? 'Preço Líq. da sequência' : '',
            valor_total: !acessoLimitado
              ? formatCurrency(sequencia.valorLiquido, 2)
              : '',
            codigo_material: '',
            configuracao: null,
            custo_total: null,
            custo_unitario: null,
            descricao_atividade: '',
            fornecedor: '',
            nome_produto: '',
            sequencia: '',
            Origem: '',
          });

          // Total da sequência
          dadosPlanilha.push({
            codigo_atividade: '',
            descricao_material: '',
            quantidade: null,
            valor_unitario: !acessoLimitado ? 'Preço total da sequência' : '',
            valor_total: !acessoLimitado
              ? formatCurrency(sequencia.valorTotal, 2)
              : '',
            codigo_material: '',
            configuracao: null,
            custo_total: null,
            custo_unitario: null,
            descricao_atividade: '',
            fornecedor: '',
            nome_produto: '',
            sequencia: '',
            Origem: '',
          });

          // Espaço entre sequências
          if (index < array.length - 1) {
            dadosPlanilha.push({
              codigo_atividade: '',
              descricao_material: '',
              quantidade: '',
              valor_unitario: '',
              valor_total: '',
              codigo_material: '',
              configuracao: null,
              custo_total: null,
              custo_unitario: null,
              descricao_atividade: '',
              fornecedor: '',
              nome_produto: '',
              sequencia: '',
              Origem: '',
            });
          }
          // }
        });

        // if (acessoLimitado) {
        // Total líquido da atividade
        dadosPlanilha.push({
          codigo_atividade: '',
          descricao_material: '',
          quantidade: null,
          valor_unitario: !acessoLimitado
            ? `Total atividade ${atividade.atividade}`
            : '',
          valor_total: !acessoLimitado
            ? formatCurrency(atividade.totalAtividade, 2)
            : '',
          codigo_material: '',
          configuracao: null,
          custo_total: null,
          custo_unitario: null,
          descricao_atividade: '',
          fornecedor: '',
          nome_produto: '',
          sequencia: '',
          Origem: '',
        });

        // Preço total da atividade
        dadosPlanilha.push({
          codigo_atividade: '',
          descricao_material: '',
          quantidade: null,
          valor_unitario: !acessoLimitado
            ? `Preço total da atividade ${atividade.atividade}`
            : '',
          valor_total: !acessoLimitado
            ? formatCurrency(atividade.valorTotalAtividade, 2)
            : '',
          codigo_material: '',
          configuracao: null,
          custo_total: null,
          custo_unitario: null,
          descricao_atividade: '',
          fornecedor: '',
          nome_produto: '',
          sequencia: '',
          Origem: '',
        });
        // }
      });

      // Total da configuração
      const totalConfiguracao = configuracao.atividades.reduce(
        (total, atividade) => total + (atividade.valorTotalAtividade ?? 0),
        0,
      );

      insereTotalGeral(totalConfiguracao);
    });
  }

  function exportarPlanilhaTabulada() {
    const dados: any[] = [];

    propostaConfiguracoes.forEach(propostaConfiguracao => {
      const configuracao = materiaisParaDownload.find(
        item => item.configuracaoId === propostaConfiguracao.orcamentoId,
      );

      if (!configuracao) {
        return;
      }

      configuracao.atividades.forEach(atividade => {
        atividade.sequencias.forEach(sequencia => {
          sequencia.sequenciaItems.forEach(material => {
            const {
              material: dadosMaterial,
              quantidade,
              valor,
              valorTotal,
            } = material;

            const { diteCodigo, codigoSap, descricao } = dadosMaterial;

            dados.push({
              configuracao: propostaConfiguracao.orcamentoId,
              codigo_atividade: atividade.produtoAtividadeId,
              nome_produto: material.produtoAtividade.significado,
              descricao_atividade: atividade.atividade,
              sequencia: String(sequencia.sequencia),
              codigo_material: codigoSap ?? diteCodigo,
              descricao_material: descricao,
              quantidade: arredondaDecimais(quantidade, 3),
              valor_unitario: !acessoLimitado ? valor ?? 0 : '',
              valor_total: !acessoLimitado ? valorTotal ?? 0 : '',
              custo_unitario: !acessoLimitado
                ? material.custoUnitario ?? 0
                : '',
              custo_total: !acessoLimitado
                ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  material.custoTotalImp ?? 0
                : '',
              fornecedor: material.material.fabrNome,
              // Origem: origem,
            });
          });
        });
      });
    });

    const nomeArquivo = `Materiais - Proposta ${propostaVersao.propostaId} (v${propostaVersao.versao})`;

    exportToSheet(
      dados,
      nomeArquivo,
      true,
      ['valor_unitario', 'valor_total', 'custo_unitario', 'custo_total'],
      ['valor_total', 'custo_total'],
    );
  }

  function insereMateriaisAvulsosDadosPlanilha() {
    const queryAvulsos: PropostaMaterialAvulso[] | undefined =
      queryClient.getQueryData([
        'proposta-configuracao-materiais-avulsos',
        propostaVersao.id,
      ]);
    if (!queryAvulsos) {
      return;
    }

    insereCabecalhoConfiguracao('Materiais avulsos da proposta');
    queryAvulsos.forEach(avulso =>
      insereItem({ ...avulso, criadoManualmente: true }),
    );
    const valorTotalAvulsos = queryAvulsos.reduce(
      (total, item) => total + Number(item.valorTotal || 0),
      0,
    );
    insereTotalGeral(valorTotalAvulsos);
  }

  function getTipoHeader(tipo: any) {
    if (String(tipo.tipoImportacao).toUpperCase() === 'OUTROS') {
      return tipo.finalidade ?? '';
    }
    if (String(tipo.tipoImportacao).toUpperCase() === 'BM') {
      return 'Bombeamento Mestre';
    }

    return tipo.tipo;
  }

  function insereMateriaisImportadosDadosPlanilha() {
    const queryImportados: any[] | undefined = queryClient.getQueryData([
      'proposta-configuracao-materiais-importados',
      propostaVersao.id,
    ]);

    if (!queryImportados) {
      return;
    }

    queryImportados.forEach((importado: any) => {
      insereCabecalhoConfiguracao(
        `${getTipoHeader(importado)} | ${importado.titulo} | ${importado.data}`,
      );

      if (
        importado.PropostaMaterialAvulso &&
        importado.PropostaMaterialAvulso.length
      ) {
        importado.PropostaMaterialAvulso.forEach((item: any) =>
          insereItem(item),
        );

        insereTotalGeral(importado?.valorTotal);
      }
    });
  }

  async function exportarPlanilha() {
    // limpa o array
    dadosPlanilha.splice(0, dadosPlanilha.length);

    insereConfiguracaoPropostaDadosPlanilha();
    insereMateriaisImportadosDadosPlanilha();
    insereMateriaisAvulsosDadosPlanilha();
    const nomeArquivo = `Materiais - Proposta ${propostaVersao.propostaId} (v${propostaVersao.versao})`;
    exportToSheet(dadosPlanilha, nomeArquivo, false, [
      'valor_unitario',
      'valor_total',
      'custo_unitario',
      'custo_total',
    ]);
  }

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        width: '100%',
      }}
    >
      <Tooltip title="Exportar tabela com suas sequências e totais.">
        <Button
          disabled={loadingMateriais}
          size="sm"
          className="btn-icon btn-2"
          style={{
            backgroundColor: '#1a8c5b',
            flex: '1 1 200px',
            margin: 0,
          }}
          color="success"
          type="button"
          onClick={exportarPlanilha}
        >
          <FaFileExcel />
          <span className="btn-inner--text">Exportação Detalhada</span>
        </Button>
      </Tooltip>

      <Tooltip title="Exportar tabela com dados tabulados sem informação de totais.">
        <Button
          disabled={loadingMateriais}
          size="sm"
          className="btn-icon btn-2"
          color="success"
          style={{
            flex: '1 1 200px',
            margin: 0,
          }}
          type="button"
          onClick={exportarPlanilhaTabulada}
        >
          <FaFileExcel />
          <span className="btn-inner--text">Exportação Tabulada</span>
        </Button>
      </Tooltip>
    </div>
  );
}
